/**
 * SessionRecoveryBanner - Componente de notificação de sessão recuperada
 * @module features/chat/components/SessionRecoveryBanner
 *
 * Exibe banner informativo quando uma sessão anterior é recuperada do LocalStorage.
 * Auto-dismiss após 5 segundos por padrão, com opção de fechar manualmente.
 *
 * T005-FE: Lógica de Recuperação de Sessão
 *
 * Features:
 * - Auto-dismiss configurável (default: 5s)
 * - Dismiss manual via botão ou tecla Escape
 * - Acessível (role="alert", aria-live, keyboard navigation)
 * - Estilo de sucesso (verde) para sessão recuperada
 */

import { cn } from '@/lib/utils';
import { CheckCircle2, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useSessionRecovery } from '../hooks/useSessionRecovery';

// =============================================================================
// TYPES
// =============================================================================

export interface SessionRecoveryBannerProps {
  /**
   * Tempo em ms para auto-dismiss (default: 5000ms)
   * Use 0 para desabilitar auto-dismiss
   */
  autoDismissMs?: number;

  /**
   * Callback chamado quando o banner é fechado
   */
  onDismiss?: () => void;

  /**
   * Classes CSS adicionais
   */
  className?: string;
}

// =============================================================================
// CONSTANTS
// =============================================================================

const DEFAULT_AUTO_DISMISS_MS = 5000;

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * Banner de notificação para sessão recuperada
 *
 * Exibe automaticamente quando useSessionRecovery detecta uma sessão
 * anterior válida no LocalStorage.
 *
 * @example
 * ```tsx
 * // Uso básico
 * <SessionRecoveryBanner />
 *
 * // Com callback
 * <SessionRecoveryBanner onDismiss={() => console.log('Fechado')} />
 *
 * // Com auto-dismiss customizado
 * <SessionRecoveryBanner autoDismissMs={3000} />
 *
 * // Sem auto-dismiss
 * <SessionRecoveryBanner autoDismissMs={0} />
 * ```
 */
export function SessionRecoveryBanner({
  autoDismissMs = DEFAULT_AUTO_DISMISS_MS,
  onDismiss,
  className,
}: SessionRecoveryBannerProps) {
  const { isLoading, isRecovered } = useSessionRecovery();
  const [isVisible, setIsVisible] = useState(true);

  /**
   * Fecha o banner e chama callback
   */
  const handleDismiss = useCallback(() => {
    setIsVisible(false);
    onDismiss?.();
  }, [onDismiss]);

  /**
   * Handler para tecla Escape
   */
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleDismiss();
      }
    },
    [handleDismiss]
  );

  /**
   * Auto-dismiss após tempo configurado
   */
  useEffect(() => {
    if (!isRecovered || !isVisible || autoDismissMs <= 0) {
      return;
    }

    const timer = setTimeout(() => {
      handleDismiss();
    }, autoDismissMs);

    return () => {
      clearTimeout(timer);
    };
  }, [isRecovered, isVisible, autoDismissMs, handleDismiss]);

  // Não renderiza se carregando, não recuperada, ou já fechada
  if (isLoading || !isRecovered || !isVisible) {
    return null;
  }

  return (
    <div
      role="alert"
      aria-live="polite"
      tabIndex={-1}
      onKeyDown={handleKeyDown}
      className={cn(
        // Layout
        'flex items-center justify-between gap-3 p-4 rounded-lg',
        // Colors - success/green theme
        'bg-green-50 border border-green-200 text-green-800',
        'dark:bg-green-950 dark:border-green-800 dark:text-green-200',
        // Animation
        'animate-in fade-in slide-in-from-top-2 duration-300',
        // Focus
        'focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2',
        className
      )}
    >
      {/* Icon and Message */}
      <div className="flex items-center gap-3">
        <CheckCircle2
          className="h-5 w-5 text-green-600 dark:text-green-400 shrink-0"
          aria-hidden="true"
        />
        <div className="flex flex-col">
          <span className="font-medium text-sm">Sessão recuperada</span>
          <span className="text-sm text-green-700 dark:text-green-300">
            Você pode continuar de onde parou.
          </span>
        </div>
      </div>

      {/* Dismiss Button */}
      <button
        type="button"
        onClick={handleDismiss}
        aria-label="Fechar notificação"
        className={cn(
          'p-1.5 rounded-md shrink-0',
          'text-green-600 hover:text-green-800',
          'dark:text-green-400 dark:hover:text-green-200',
          'hover:bg-green-100 dark:hover:bg-green-900',
          'focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2',
          'transition-colors duration-150'
        )}
      >
        <X className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  );
}

export default SessionRecoveryBanner;
